/**
 * @file dcp/src/dcp-client/worker/supervisor2/protocol-manager.js
 *
 * A support class for Supervisor2.
 * It is for managing protocol connections and messaging.
 *
 * @author Paul, paul@distributive.network
 * @date Feb-April 2023,
 * @module protocol-manager
 * @copyright Copyright (c) 2018-2025, Distributive Corp. All Rights Reserved
 */
/* global dcpConfig */ // eslint-disable-line no-redeclare
// @ts-check
'use strict';

const proto4 = require('dcp/protocol');
const { DCPError } = require('dcp/common/dcp-error');
const common = require('./common');
const { checkConnectionName, selectiveDebug, selectiveDebug2 } = common;
const utils = require('dcp/utils');

/** @typedef {import('./slice2').Slice} Slice                             */
/** @typedef {import('./options').SupervisorOptions} SupervisorOptions    */
/** @typedef {import('./index').Supervisor} Supervisor                    */
/** @typedef {import('dcp/common/concurrency').Synchronizer} Synchronizer */
/** @typedef {import('dcp/dcp-client/wallet/keystore').Keystore} Keystore */
/** @typedef {import('dcp/protocol/connection').Connection} Connection */

/**
 * class Connector will retry any failed request.
 * Requires protocol version# >= 5.3.
 * It relies on the protocol backoff meachnism for retries.
 */
class Connector
{
  /**
   * @param {string} which              name of connection
   */
  constructor (which)
  {
    if (which === 'packageManager')
      this.targetDescriptor = dcpConfig.packageManager;
    else
      this.targetDescriptor = dcpConfig.scheduler.services[which];

    this.which       = which;
    /** @type {Array<*>} */
    this.pendingData = [];
    /** @type {Connection} */
    this.connection  = null;
    /** @type {{ onEnd, onReady }} */
    this.handlers = {
      onEnd: this.connect.bind(this),
      onReady: this.#sendPendingData.bind(this),
    };
  }
  get id () { return this.connection?.id ?? -1; }
  connect ()
  {
    selectiveDebug() && console.debug('Connector.connect', this.which);
    this.connection = new proto4.Connection(this.targetDescriptor);
    this.connection.on('ready', this.handlers.onReady);
    this.connection.on('end',   this.handlers.onEnd);
  }
  close ()
  {
    selectiveDebug() && console.debug('Connector.close', this.which);
    // Do a call to #sendPendingData just in case the very last request failed.
    return this.#sendPendingData().finally(() => {
      this.connection.off('end',   this.handlers.onEnd);
      this.connection.off('ready', this.handlers.onReady);
      this.connection.close();
    });
  }
  keepalive () { return this.connection.keepalive(); }
  request (operation, data)
  {
    return this.connection.request(operation, data)
      .then((response) => {
        if (this.pendingData.length > 0)
          this.#sendPendingData();
        return response;
      }).catch((error) => {
        this.pendingData.push({ operation, data });
        selectiveDebug() && console.debug(`Connector(${this.which},${operation}): connection.request failed:`, error);
        throw error;
    });
  }
  #sendPendingData ()
  {
    if (this.pendingData.length < 1)
      return Promise.resolve();
    const promises = [];
    const messages = this.pendingData.slice();
    this.pendingData.length = 0;
    for (let i = 0; i < messages.length; i++)
      promises.push(this.request(messages[i].operation, messages[i].data));
    return Promise.all(promises);
  }
}
/**
 * Connection management.
 */
class ProtocolManager
{
  /**
   * @param {Supervisor} supervisor
   */
  constructor(supervisor)
  {
    this.supervisor = supervisor;
    this.lastDcpsid = null;
    /** @type {Connector} */
    this.taskDistributor = null;
    /** @type {Connector} */
    this.resultSubmitter = null;
    /** @type {Connector} */
    this.eventRouter     = null;
    /** @type {Connector} */
    this.packageManager  = null;
  }

  /** @type {SupervisorOptions} */
  get options () { return this.supervisor.options; }
  /** @type {Synchronizer} */
  get state () { return this.supervisor.state; }

  /**
   * Connect the supervisor to a given scheduler sub-service.
   * @param {string} which -- 'taskDistributor', 'resultSubmitter', 'eventRouter' or 'packageManager'.
   */
  connectTo(which)
  {
    checkConnectionName(which);
    this[which] = new Connector(which);
    this[which].connect();
    this[which].connection.on('connect', (event) => this.supervisor.worker.emit('connect', event));
    this[which].connection.on('disconnect', (event) => this.supervisor.worker.emit('disconnect', event));

    selectiveDebug() && console.debug(`Opening new connection ${this[which].id} to ${which} at ${this[which].targetDescriptor.location.href}.`,
                                      Date.now() - this.supervisor.lastTime);
  }

  /**
   * Close a connection properly.
   * @param {string} which -- Name of the connection to close.
   */
  async closeConnection (which)
  {
    if (this[which])
    {
      checkConnectionName(which);
      return this[which].close();
    }
  }

  /**
   * Close all connections.
   */
  closeConnections ()
  {
    return Promise.all([
      this.closeConnection('taskDistributor'),
      this.closeConnection('resultSubmitter'),
      this.closeConnection('eventRouter'),
      this.closeConnection('packageManager')
    ]);
  }

  /**
   * Open all connections. Called only in Supervisor.work() because the Connector class
   * is responsible for recreating whenever the 'end' event fires.
   */
  instantiateAllConnections ()
  {
    this.connectTo('taskDistributor');
    this.connectTo('eventRouter');
    this.connectTo('resultSubmitter');
    this.connectTo('packageManager');
  }

  /**
   * Safely send return slices to RS status.
   * @param {Slice[]}      slices - The slice candidates to check if they can be returned to the scheduler.
   * @param {string|{message: string|false, errorCode: string}} reason
   * @returns {Promise<*>}
   */
  async sliceReturn (slices, reason)
  {
    const failedRequest = (error) => {
      error = new DCPError('Result submitter connection failed: will try again on new connection.', error);
      selectiveDebug() && console.error(`Failed to return slices ${utils.compressSlices(slices)}`, error);
      slices.forEach((slice) => { this.jobEmit(slice, 'return', error, slice.sliceNumber); });
    };
    try
    {
      for (let k = 0; k < slices.length; k++)
        this.jobEmit(slices[k], 'beforeReturn', slices[k].sliceNumber);

      const payload = common.compressPayload(this.supervisor.workerId, slices, reason);
      return this.resultSubmitter.request('status', payload)
        .then((resp) => {
          for (let k = 0; k < slices.length; k++)
          {
            // Emit 'return' event with (Error shaped) work.reject info, when it exists.
            if (slices[k].rejectedState)
              this.jobEmit(slices[k], 'return', slices[k].rejectedState.info, slices[k].sliceNumber);
            else
              this.jobEmit(slices[k], 'return', slices[k].sliceNumber);
          }
          return resp;
        })
        .catch(error => {
          failedRequest(error);
        });
    }
    catch (error)
    {
      failedRequest(error);
    }
  }

  /**
   * Safely send result submitter status messages.
   * @param {object} data
   * @param {string} errorMsg
   * @returns {Promise<*>}
   */
  async safeRSStatus (data, errorMsg)
  {
    try
    {
      return this.resultSubmitter.request('status', data)
        .catch(error => {
          selectiveDebug() && console.error(`safeRSStatus: ${errorMsg}; will try again on new connection.`, error);
        });
    }
    catch (error)
    {
      selectiveDebug() && console.error(`safeRSStatus: ${errorMsg}; will try again on new connection.`, error);
    }
  }

  /**
   * Safely send event router workEmit messages.
   * @param {*} data
   * @param {string} errorMsg
   * @returns {Promise<*>}
   */
  async safeWorkEmit (data, errorMsg)
  {
    try
    {
      selectiveDebug2() && console.debug(`safeWorkEmit: Before sending message: (${this.eventRouter?.id}).`);
      return this.eventRouter.request('workEmit', data)
        .catch(error => {
          selectiveDebug() && console.error(`safeWorkEmit: ${errorMsg}; will try again on new connection.`, error);
        });
    }
    catch (error)
    {
      selectiveDebug() && console.error(`safeWorkEmit: ${errorMsg}; will try again on new connection.`, error);
    }
  }

  /**
   * Safe event emitter on slice.jobHandle.
   * @param {Slice}   slice
   * @param {string}  event
   * @param  {...any} args
   */
  jobEmit(slice, event, ...args)
  {
    slice.jobHandle.emit(event, ...args);
  }
}
exports.ProtocolManager = ProtocolManager;
